import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import {
  Loader2,
  ChefHat,
  UtensilsCrossed,
  Sparkles,
  Package,
  Clock,
  Users,
  Euro,
  Pencil,
  Save,
  X,
  RefreshCw,
  Lightbulb,
  Plus,
  Trash2,
  AlertTriangle,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { formatCurrency } from "../../lib/utils";
import { convertQty, normalizeUnit, computeIngredientCost } from "../../lib/units";
import { RecipeIngredientRow } from "./RecipeIngredientRow";
import { RecipeImageSection } from "./RecipeImageSection";
import { MissingIngredientsBanner } from "./MissingIngredientsBanner";
import { QuickAddIngredientDialog } from "../inventory/QuickAddIngredientDialog";
import ProfessionalProposalDialog from "./ProfessionalProposalDialog";

/**
 * RecipeDetailDialog
 * Props:
 *   open            – boolean
 *   onOpenChange    – (open: boolean) => void
 *   selectedDish    – dish object (may include image_history)
 *   onDishUpdated   – (dishId, { image_url, image_history }) => void  — syncs parent grid
 */
export function RecipeDetailDialog({ open, onOpenChange, selectedDish, onDishUpdated, isBeverage = false, restaurantId = null }) {
  // Normalize recipe fields that the AI may return as string instead of array.
  // Fixes "Consejos del chef" showing one letter per row when tips is a string
  // or a corrupted char-array (from previous edits where a string was spread).
  const normalizeRecipe = (recipe) => {
    if (!recipe) return recipe;
    const tipsRaw = recipe.tips;
    let tips;
    if (Array.isArray(tipsRaw)) {
      const cleaned = tipsRaw.filter(t => typeof t === "string" || typeof t === "number").map(String);
      // Detect corrupted char-array (e.g. ['P','a','r','a',' ',...]): most items are single chars
      const singleCharCount = cleaned.filter(t => t.length <= 1).length;
      if (cleaned.length >= 4 && singleCharCount / cleaned.length > 0.6) {
        const joined = cleaned.join("").trim();
        tips = joined
          ? joined.split(/\n+|(?<=[.!?])\s+(?=[A-ZÁÉÍÓÚÑ])/).map(s => s.trim()).filter(Boolean)
          : [];
      } else {
        tips = cleaned.filter(t => t.trim().length > 0);
      }
    } else if (typeof tipsRaw === "string" && tipsRaw.trim()) {
      const parts = tipsRaw.split(/\n+|(?<=[.!?])\s+(?=[A-ZÁÉÍÓÚÑ])/).map(s => s.trim()).filter(Boolean);
      tips = parts.length > 0 ? parts : [tipsRaw.trim()];
    } else {
      tips = [];
    }
    return { ...recipe, tips };
  };
  const [localDish, setLocalDish] = useState(null);
  const [currentRecipe, setCurrentRecipe] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedRecipe, setEditedRecipe] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [comparingUrl, setComparingUrl] = useState(null);
  const [inventoryIngredients, setInventoryIngredients] = useState([]);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddContext, setQuickAddContext] = useState(null); // { rowIndex, initialName }
  const [autoLinking, setAutoLinking] = useState(false);
  const [autoFillingIds, setAutoFillingIds] = useState(new Set());
  const [siblingNames, setSiblingNames] = useState(new Set());
  const [proProposal, setProProposal] = useState(null);
  const [proposing, setProposing] = useState(false);
  // Unit-mismatch suggestions returned by auto-link (backend Guard 1).
  // Shape: [{ row_index, recipe_name, recipe_unit, inventory_unit, inventory_ingredient_id, inventory_name, reason:"unit_mismatch" }]
  const [unitMismatchSuggestions, setUnitMismatchSuggestions] = useState([]);
  const fileInputRef = useRef(null);

  // Reset and load recipe whenever a new dish is opened
  useEffect(() => {
    if (!selectedDish || !open) return;
    setLocalDish(selectedDish);
    setIsEditing(false);
    setCurrentRecipe(null);
    setEditedRecipe(null);
    setComparingUrl(null);
    api.getRecipe(selectedDish.dish_id)
      .then(recipe => {
        // Backend returns {exists: false} when no recipe yet (instead of 404)
        if (recipe && recipe.exists === false) {
          setCurrentRecipe(null);
          setEditedRecipe(null);
        } else {
          const normalized = normalizeRecipe(recipe);
          setCurrentRecipe(normalized);
          setEditedRecipe(normalized);
        }
      })
      .catch(() => {
        setCurrentRecipe(null);
        setEditedRecipe(null);
      });
  }, [selectedDish?.dish_id, open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load inventory ingredients (for recipe-linking) when dialog opens
  useEffect(() => {
    if (!open || !restaurantId) return;
    let cancelled = false;
    api.getIngredients(restaurantId)
      .then(list => { if (!cancelled) setInventoryIngredients(Array.isArray(list) ? list : []); })
      .catch(() => { if (!cancelled) setInventoryIngredients([]); });
    return () => { cancelled = true; };
  }, [open, restaurantId]);

  // Fetch every dish name of the restaurant to detect duplicate renames.
  // Only when editing is enabled (avoids eager network on open).
  useEffect(() => {
    if (!open || !restaurantId || !isEditing || !selectedDish?.dish_id) return;
    let cancelled = false;
    const norm = (s) => (s || "")
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .toLowerCase().trim();
    api.getAllDishesForRestaurant(restaurantId)
      .then((list) => {
        if (cancelled) return;
        const names = new Set(
          (list || [])
            .filter((d) => d.dish_id !== selectedDish.dish_id)
            .map((d) => norm(d.name)),
        );
        setSiblingNames(names);
      })
      .catch(() => { if (!cancelled) setSiblingNames(new Set()); });
    return () => { cancelled = true; };
  }, [open, restaurantId, isEditing, selectedDish?.dish_id]);

  const handleClose = (isOpen) => {
    onOpenChange(isOpen);
    if (!isOpen) {
      setIsEditing(false);
      setCurrentRecipe(null);
      setEditedRecipe(null);
      setLocalDish(null);
      setComparingUrl(null);
      setSiblingNames(new Set());
    }
  };

  // Detect duplicate name match (case + accent insensitive, trims whitespace).
  // Only evaluated while editing + with a non-empty new name distinct from the
  // original, so clicking Save without touching the field never triggers it.
  const nameConflict = (() => {
    if (!isEditing) return false;
    const typed = (editedRecipe?._dish_name || "").trim();
    if (!typed) return false;
    if (typed === (localDish?.name || "").trim()) return false;
    const norm = typed
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
    return siblingNames.has(norm);
  })();

  const handleGenerateRecipeProfessional = async () => {
    if (!localDish || !restaurantId) return;
    setProposing(true);
    try {
      toast.info("Analizando el plato con IA profesional…");
      const proposal = await api.request(
        `/restaurants/${restaurantId}/ai/recipe-professional/propose`,
        {
          method: "POST",
          body: JSON.stringify({
            dish_id: localDish.dish_id,
            dish_name: localDish.name,
            servings: 4,
          }),
        },
      );
      setProProposal(proposal);
    } catch (e) {
      toast.error(e?.message || "Error generando propuesta profesional");
    } finally {
      setProposing(false);
    }
  };

  const handleProApplied = async () => {
    // Reload recipe
    try {
      const r = await api.getRecipe(localDish.dish_id);
      setCurrentRecipe(normalizeRecipe(r));
    } catch (_e) { /* noop */ }
  };


  const handleGenerateRecipe = async () => {
    if (!localDish) return;
    // If we already have a recipe in view, this click is a regeneration
    // → bypass the existing-recipe guard and the cross-restaurant cache.
    const isRegeneration = !!(currentRecipe && (
      (currentRecipe.ingredients || []).length > 0 ||
      (currentRecipe.ai_ingredients || []).length > 0
    ));
    setGenerating(true);
    let imageUrl = localDish.image_url || null;
    try {
      toast.info(isRegeneration ? "Regenerando receta con IA..." : "Generando receta con IA...");
      const recipe = await api.aiGenerateRecipe(
        { dish_id: localDish.dish_id, dish_name: localDish.name, servings: currentRecipe?.servings || 4 },
        localDish.restaurant_id,
        { force: isRegeneration },
      );
      // Guard: backend returned a skip signal (beverage, no-op cache, etc.)
      if (recipe?.skipped) {
        toast.info(recipe.message || "No se regeneró la receta");
        return;
      }
      if (!imageUrl) {
        setGeneratingImage(true);
        toast.info("Generando imagen del plato con IA...");
        try {
          const imageResult = await api.generateDishImageAI(
            localDish.name,
            localDish.description || recipe.dish_name
          );
          if (imageResult.success) imageUrl = imageResult.image_url;
        } catch (imgError) {
        } finally {
          setGeneratingImage(false);
        }
      }
      const newRecipe = {
        dish_id: localDish.dish_id,
        ingredients: recipe.ingredients || [],
        preparation_steps: recipe.steps || [],
        preparation_time: recipe.preparation_time || 30,
        servings: recipe.servings || 4,
        tips: Array.isArray(recipe.tips)
          ? recipe.tips.filter(t => typeof t === "string" || typeof t === "number").map(String)
          : (typeof recipe.tips === "string" && recipe.tips.trim()
              ? recipe.tips.split(/\n+|(?<=[.!?])\s+(?=[A-ZÁÉÍÓÚÑ])/).map(s => s.trim()).filter(Boolean)
              : []),
        image_url: imageUrl,
        total_cost: recipe.estimated_total_cost || 0,
        cost_per_serving: (recipe.estimated_total_cost || 0) / (recipe.servings || 1),
      };
      toast.info("Guardando receta...");
      try {
        const savedRecipe = await api.createRecipe(localDish.dish_id, {
          dish_id: localDish.dish_id,
          ingredients: [],
          preparation_steps: newRecipe.preparation_steps,
          preparation_time: newRecipe.preparation_time,
          servings: newRecipe.servings,
          tips: newRecipe.tips,
          image_url: newRecipe.image_url,
          ai_ingredients: newRecipe.ingredients,
        });
        const completeRecipe = {
          ...savedRecipe,
          ingredients: newRecipe.ingredients,
          tips: newRecipe.tips,
          image_url: newRecipe.image_url,
        };
        setCurrentRecipe(completeRecipe);
        setEditedRecipe(completeRecipe);
        // Report auto-link results from backend (Feature #59 — Fase 2)
        const autoLink = recipe?.auto_link;
        if (autoLink?.linked > 0) {
          toast.success(`Receta generada · ${autoLink.linked} ingrediente${autoLink.linked > 1 ? "s" : ""} vinculado${autoLink.linked > 1 ? "s" : ""} al inventario`, {
            description: autoLink.suggestions?.length
              ? `${autoLink.suggestions.length} sugerencia${autoLink.suggestions.length > 1 ? "s" : ""} requieren revisión manual`
              : undefined,
          });
        } else {
          toast.success("Receta generada y guardada correctamente");
        }
        setUnitMismatchSuggestions(
          Array.isArray(autoLink?.suggestions)
            ? autoLink.suggestions.filter(s => s?.reason === "unit_mismatch")
            : []
        );
      } catch (saveError) {
        console.error("Error saving recipe:", saveError);
        setCurrentRecipe(newRecipe);
        setEditedRecipe(newRecipe);
        toast.warning("Receta generada pero no se pudo guardar automáticamente");
      }
    } catch (error) {
      console.error("Recipe generation error:", error);
      toast.error("Error al generar receta. Por favor intenta de nuevo.");
    } finally {
      setGenerating(false);
      setGeneratingImage(false);
    }
  };

  const handleAIAutofillAndLink = async (name, rowIndex) => {
    if (!restaurantId || !name) return;
    setAutoFillingIds(prev => new Set(prev).add(name));
    try {
      const res = await api.aiAutofillIngredient(restaurantId, name, { create: true });
      const created = res?.ingredient;
      if (!created?.ingredient_id) throw new Error("No se pudo crear el ingrediente");
      // Refresh inventory (safe even if concurrent — list is idempotent)
      try {
        const list = await api.getIngredients(restaurantId);
        setInventoryIngredients(Array.isArray(list) ? list : []);
      } catch { /* noop */ }
      // Track rows that ended up with an incompatible unit so the
      // "Revisar unidad" badge can surface them to the chef.
      const mismatchRows = [];
      // Patch ALL rows that match this name (same ingredient used twice
      // in recipe → both get linked). FUNCTIONAL setState so parallel calls
      // don't overwrite each other's changes.
      setEditedRecipe(prev => {
        if (!prev) return prev;
        const ings = [...(prev.ai_ingredients || prev.ingredients || [])];
        const baseUnit = created.unit || "g";
        const cpu = parseFloat(created.cost_per_unit) || 0;
        const wasteMult = 1 + (parseFloat(created.waste_percentage) || 0) / 100;
        const targetKey = (name || "").trim().toLowerCase();
        ings.forEach((row, i) => {
          if (!row || row.ingredient_id) return;
          const rowName = (row.name || "").trim().toLowerCase();
          // Patch the explicit rowIndex + any row with the same name
          if (i === rowIndex || rowName === targetKey) {
            const qtyRow = parseFloat(row.quantity) || 0;
            const unitRow = row.unit || "";
            const fu = normalizeUnit(unitRow);
            const tu = normalizeUnit(baseUnit);
            const { qty: qtyBase, ok } = convertQty(qtyRow, unitRow, baseUnit);

            if (!ok && fu && tu && fu !== tu) {
              // Non-convertible (e.g. "ud" vs "kg") — LINK the id but
              // DO NOT mutate qty/unit. Flag for manual review via badge.
              ings[i] = {
                ...row,
                name: created.name,
                ingredient_id: created.ingredient_id,
                // keep original quantity + unit untouched
                _cost_missing: cpu <= 0 ? true : row._cost_missing,
              };
              mismatchRows.push({
                row_index: i,
                recipe_name: row.name || created.name,
                recipe_unit: unitRow,
                inventory_unit: baseUnit,
                inventory_ingredient_id: created.ingredient_id,
                inventory_name: created.name,
                reason: "unit_mismatch",
              });
            } else {
              // Same unit OR safely convertible (g↔kg / ml↔l).
              // Convert qty and preserve chef's original display values.
              const newRow = {
                ...row,
                name: created.name,
                ingredient_id: created.ingredient_id,
                quantity: Math.round(qtyBase * 10000) / 10000,
                unit: baseUnit,
                _display_quantity: qtyRow,
                _display_unit: unitRow || baseUnit,
                _cost_missing: cpu <= 0,
              };
              // Centralized cost helper — single source of truth
              const computed = computeIngredientCost(newRow, created);
              ings[i] = {
                ...newRow,
                estimated_cost: cpu > 0 ? computed : (row.estimated_cost ?? 0),
              };
            }
          }
        });
        return { ...prev, ai_ingredients: ings, ingredients: ings };
      });
      // Push local unit-mismatch suggestions so badges render
      if (mismatchRows.length > 0) {
        setUnitMismatchSuggestions(prev => {
          const keys = new Set(prev.map(s => `${s.row_index}|${(s.recipe_name || "").toLowerCase()}`));
          const fresh = mismatchRows.filter(m =>
            !keys.has(`${m.row_index}|${(m.recipe_name || "").toLowerCase()}`)
          );
          return [...prev, ...fresh];
        });
      }
      toast.success(`"${created.name}" creado y vinculado`, {
        description: mismatchRows.length > 0
          ? `⚠️ ${mismatchRows.length} fila${mismatchRows.length > 1 ? "s" : ""} con unidad incompatible — revisa el badge ámbar`
          : `${created.unit} · ${(parseFloat(created.cost_per_unit) || 0).toFixed(2)} €/${created.unit} · merma ${created.waste_percentage}%`,
      });
      return { ok: true, created };
    } catch (err) {
      toast.error(err?.message || "Error al crear el ingrediente");
      return { ok: false, error: err };
    } finally {
      setAutoFillingIds(prev => {
        const next = new Set(prev);
        next.delete(name);
        return next;
      });
    }
  };

  const handleAIAutofillAll = async () => {
    const ings = editedRecipe?.ai_ingredients || editedRecipe?.ingredients || [];
    const pending = ings
      .map((ing, idx) => ({ name: (ing?.name || "").trim(), idx }))
      .filter(({ name, idx }) => name && !ings[idx]?.ingredient_id);
    if (pending.length === 0) return;
    // Parallel autofill with Promise.allSettled — LLM calls are IO-bound.
    // If a call fails, others still complete independently.
    await Promise.allSettled(
      pending.map(({ name, idx }) => handleAIAutofillAndLink(name, idx))
    );
  };

  const handleAutoLink = async () => {
    if (!restaurantId || !localDish?.dish_id || autoLinking) return;
    setAutoLinking(true);
    try {
      const res = await api.autoLinkRecipeIngredients(restaurantId, localDish.dish_id, 0.75);
      // Reload recipe fresh from backend so UI reflects persisted links
      const updatedRecipe = await api.getRecipe(localDish.dish_id);
      const normalized = normalizeRecipe(updatedRecipe);
      setCurrentRecipe(normalized);
      setEditedRecipe(normalized);

      if (res.linked > 0 && res.suggestions?.length > 0) {
        toast.success(`${res.linked} ingredientes vinculados automáticamente`, {
          description: `${res.suggestions.length} sugerencia${res.suggestions.length > 1 ? "s" : ""} requieren revisión manual.`,
        });
      } else if (res.linked > 0) {
        toast.success(`${res.linked} ingredientes vinculados al inventario`);
      } else if (res.suggestions?.length > 0) {
        toast.info(`${res.suggestions.length} sugerencias encontradas — abre el editor para confirmar manualmente`);
      } else {
        toast.info("Ningún ingrediente pudo vincularse automáticamente");
      }
      // Persist unit-mismatch suggestions so each row can show a badge
      setUnitMismatchSuggestions(
        Array.isArray(res.suggestions)
          ? res.suggestions.filter(s => s?.reason === "unit_mismatch")
          : []
      );
    } catch (err) {
      console.error("Auto-link error:", err);
      toast.error(err?.message || "Error al vincular con inventario");
    } finally {
      setAutoLinking(false);
    }
  };

  const handleSaveRecipe = async () => {
    if (!editedRecipe || !localDish) return;
    setSaving(true);
    try {
      // Persist dish name if the user edited it
      const newName = (editedRecipe?._dish_name ?? localDish.name ?? "").trim();
      if (newName && newName !== localDish.name) {
        await api.updateDish(localDish.dish_id, {
          name: newName,
          description: localDish.description || "",
          price: localDish.price,
          category_id: localDish.category_id,
          is_active: localDish.is_active !== false,
          image_url: localDish.image_url,
          allergens: localDish.allergens || [],
          tags: localDish.tags || [],
        });
        setLocalDish(prev => ({ ...prev, name: newName }));
        onDishUpdated?.(localDish.dish_id, { name: newName });
      }
      if (currentRecipe?.recipe_id) {
        const ings = editedRecipe.ai_ingredients || editedRecipe.ingredients || [];
        await api.updateRecipe(currentRecipe.recipe_id, {
          ingredients: ings,
          ai_ingredients: ings,
          preparation_steps: editedRecipe.preparation_steps || editedRecipe.steps,
          preparation_time: editedRecipe.preparation_time,
          servings: editedRecipe.servings,
          tips: editedRecipe.tips,
          image_url: editedRecipe.image_url,
        });
        toast.success("Receta actualizada");
      } else {
        const ings = editedRecipe.ai_ingredients || editedRecipe.ingredients || [];
        // Send ai_ingredients (full shape with name/quantity/unit/estimated_cost
        // + optional ingredient_id/format_id) so backend persists the linked data.
        const recipeData = {
          dish_id: localDish.dish_id,
          ingredients: [], // legacy inventory-linked path; we use ai_ingredients
          ai_ingredients: ings,
          preparation_steps: editedRecipe.preparation_steps || editedRecipe.steps || [],
          preparation_time: editedRecipe.preparation_time || 30,
          servings: editedRecipe.servings || 4,
        };
        const created = await api.request(`/dishes/${localDish.dish_id}/recipe`, {
          method: "POST",
          body: JSON.stringify(recipeData),
        });
        if (created.recipe_id) {
          await api.updateRecipe(created.recipe_id, {
            ingredients: ings,
            ai_ingredients: ings,
            tips: editedRecipe.tips,
            image_url: editedRecipe.image_url,
          });
        }
        toast.success("Receta guardada");
      }
      const updatedRecipe = await api.getRecipe(localDish.dish_id);
      const normalized = normalizeRecipe(updatedRecipe);
      setCurrentRecipe(normalized);
      setEditedRecipe(normalized);
      setIsEditing(false);
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Error al guardar receta");
    } finally {
      setSaving(false);
    }
  };

  const handleUploadImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Tipo de archivo no válido. Usa JPEG, PNG o WebP.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Imagen demasiado grande. Máximo 5MB.");
      return;
    }
    setUploading(true);
    try {
      const uploadResult = await api.uploadImage(file);
      if (uploadResult.success) {
        const newUrl = uploadResult.image_url;
        const updatedDish = await api.updateDishImage(localDish.dish_id, newUrl);
        setLocalDish(updatedDish);
        setEditedRecipe(prev => prev ? { ...prev, image_url: newUrl } : prev);
        setCurrentRecipe(prev => prev ? { ...prev, image_url: newUrl } : prev);
        if (currentRecipe?.recipe_id) {
          await api.updateRecipe(currentRecipe.recipe_id, { image_url: newUrl });
        }
        onDishUpdated(localDish.dish_id, {
          image_url: updatedDish.image_url,
          image_history: updatedDish.image_history || [],
        });
        toast.success("Foto actualizada correctamente");
      } else {
        toast.error("Error al subir la imagen");
      }
    } catch (error) {
      toast.error("Error al subir la imagen: " + error.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRestoreImage = async (url) => {
    if (!localDish || uploading) return;
    setUploading(true);
    try {
      const updatedDish = await api.updateDishImage(localDish.dish_id, url);
      setLocalDish(updatedDish);
      setEditedRecipe(prev => prev ? { ...prev, image_url: url } : prev);
      setCurrentRecipe(prev => prev ? { ...prev, image_url: url } : prev);
      if (currentRecipe?.recipe_id) {
        await api.updateRecipe(currentRecipe.recipe_id, { image_url: url });
      }
      onDishUpdated(localDish.dish_id, {
        image_url: updatedDish.image_url,
        image_history: updatedDish.image_history || [],
      });
      toast.success("Imagen restaurada");
    } catch {
      toast.error("Error al restaurar imagen");
    } finally {
      setUploading(false);
    }
  };

  // ── Ingredient helpers ────────────────────────────────────────────────────
  const updateIngredient = (index, field, value) => {
    const newIngredients = [...(editedRecipe.ai_ingredients || editedRecipe.ingredients || [])];
    newIngredients[index] = { ...newIngredients[index], [field]: value };
    setEditedRecipe(prev => ({ ...prev, ai_ingredients: newIngredients, ingredients: newIngredients }));
  };
  const removeIngredient = (index) => {
    setEditedRecipe(prev => {
      const next = (prev.ai_ingredients || prev.ingredients || []).filter((_, i) => i !== index);
      return { ...prev, ai_ingredients: next, ingredients: next };
    });
  };
  const addIngredient = () => {
    setEditedRecipe(prev => {
      const next = [...(prev.ai_ingredients || prev.ingredients || []), { name: "", quantity: 0, unit: "g", estimated_cost: 0 }];
      return { ...prev, ai_ingredients: next, ingredients: next };
    });
  };

  // ── Step helpers ──────────────────────────────────────────────────────────
  const updateStep = (index, value) => {
    const steps = [...(editedRecipe.preparation_steps || editedRecipe.steps || [])];
    steps[index] = value;
    setEditedRecipe(prev => ({ ...prev, preparation_steps: steps, steps }));
  };
  const removeStep = (index) => {
    const steps = (editedRecipe.preparation_steps || editedRecipe.steps || []).filter((_, i) => i !== index);
    setEditedRecipe(prev => ({ ...prev, preparation_steps: steps, steps }));
  };
  const addStep = () => {
    const steps = [...(editedRecipe.preparation_steps || editedRecipe.steps || []), ""];
    setEditedRecipe(prev => ({ ...prev, preparation_steps: steps, steps }));
  };

  // ── Tip helpers ───────────────────────────────────────────────────────────
  const updateTip = (index, value) => {
    const currentTips = Array.isArray(editedRecipe.tips) ? editedRecipe.tips : [];
    const newTips = [...currentTips];
    newTips[index] = value;
    setEditedRecipe(prev => ({ ...prev, tips: newTips }));
  };
  const removeTip = (index) => {
    setEditedRecipe(prev => {
      const currentTips = Array.isArray(prev.tips) ? prev.tips : [];
      return { ...prev, tips: currentTips.filter((_, i) => i !== index) };
    });
  };
  const addTip = () => {
    setEditedRecipe(prev => {
      const currentTips = Array.isArray(prev.tips) ? prev.tips : [];
      return { ...prev, tips: [...currentTips, ""] };
    });
  };

  const displayRecipe = isEditing ? editedRecipe : currentRecipe;
  const imgSrc = localDish?.image_url || displayRecipe?.image_url;

  // ── Beverage-specific handlers ────────────────────────────────────────
  const bevAbortRef = useRef(null);
  const [bevRegenLoading, setBevRegenLoading] = useState(false);

  const handleRegenerateBeverageImage = async () => {
    if (!localDish) return;
    // Abort any previous request
    try { bevAbortRef.current?.abort(); } catch { /* noop */ }
    const ctrl = new AbortController();
    bevAbortRef.current = ctrl;
    setBevRegenLoading(true);
    try {
      const result = await api.generateDishImageSync(localDish.dish_id, { signal: ctrl.signal });
      if (ctrl.signal.aborted) return;
      const newUrl = result?.image_url;
      if (newUrl) {
        // Cache-bust so <img> reloads immediately
        const busted = `${newUrl}${newUrl.includes("?") ? "&" : "?"}_t=${Date.now()}`;
        setLocalDish(prev => prev ? { ...prev, image_url: busted, image_source: "ai" } : prev);
        onDishUpdated?.(localDish.dish_id, { image_url: newUrl, image_source: "ai" });
        toast.success("Imagen regenerada");
      } else {
        toast.error("La IA no devolvió imagen");
      }
    } catch (err) {
      if (err?.name === "AbortError" || ctrl.signal.aborted) {
        toast.info("Regeneración cancelada");
      } else {
        toast.error(err?.message || "Error al regenerar imagen");
      }
    } finally {
      bevAbortRef.current = null;
      setBevRegenLoading(false);
    }
  };

  const handleCancelBeverageRegen = () => {
    try { bevAbortRef.current?.abort(); } catch { /* noop */ }
  };

  const handleSaveBeverage = async () => {
    if (!localDish) return;
    setSaving(true);
    try {
      const newName = (editedRecipe?._dish_name ?? localDish.name ?? "").trim();
      const newDesc = editedRecipe?._dish_description ?? localDish.description ?? "";
      if (!newName) {
        toast.error("El nombre no puede estar vacío");
        setSaving(false);
        return;
      }
      const payload = { description: newDesc };
      if (newName !== localDish.name) payload.name = newName;
      const updated = await api.updateDish(localDish.dish_id, payload);
      setLocalDish(prev => prev ? { ...prev, ...updated } : prev);
      onDishUpdated?.(localDish.dish_id, { name: newName, description: newDesc });
      setIsEditing(false);
      toast.success("Bebida actualizada");
    } catch (err) {
      toast.error(err?.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ChefHat className="w-5 h-5 text-primary" />
            {isBeverage ? `Editar: ${localDish?.name}` : `Receta: ${localDish?.name}`}
          </DialogTitle>
          <DialogDescription>
            {isBeverage
              ? "Edita los detalles de la bebida"
              : (currentRecipe ? "Visualiza y edita la receta" : "Genera una receta con IA")}
          </DialogDescription>
        </DialogHeader>

        {/* No recipe yet — only meaningful for dishes, not beverages */}
        {!displayRecipe && !generating && !isBeverage && (
          <div className="py-12 text-center">
            <UtensilsCrossed className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-heading text-lg font-semibold mb-2">Sin receta</h3>
            <p className="text-muted-foreground mb-6">
              Este plato aún no tiene receta. Elige el modo de generación:
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
              <Button
                onClick={handleGenerateRecipeProfessional}
                disabled={proposing || generating}
                className="gap-2 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white"
                data-testid="generate-pro-recipe-btn"
              >
                {proposing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Profesional (detecta recetas base)
              </Button>
              <Button
                onClick={handleGenerateRecipe}
                disabled={generating || proposing}
                variant="outline"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Receta simple
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-4 max-w-md mx-auto">
              <strong>Profesional</strong> descompone el plato en elaboraciones reutilizables
              (salsas, fondos, cremas…) como haría una cocina real. <strong>Simple</strong>
              {" "}genera la receta completa como una sola unidad.
            </p>
          </div>
        )}

        {/* Legacy recipe → offer "Refinar a profesional" upgrade CTA.
            Visible only when there's already a simple/legacy recipe AND it's
            NOT already professional. Reuses the same propose/apply flow that
            overwrites the recipe in place (backend upsert).  */}
        {!isBeverage && !generating && displayRecipe
         && displayRecipe.recipe_type !== "final_professional"
         && (displayRecipe.base_recipe_refs || []).length === 0 && (
          <div className="border border-violet-200 dark:border-violet-900 bg-gradient-to-br from-violet-50 to-indigo-50 dark:from-violet-950/30 dark:to-indigo-950/30 rounded-lg p-3 flex flex-wrap items-center gap-3"
               data-testid="refine-legacy-banner">
            <Sparkles className="w-4 h-4 text-violet-600 flex-shrink-0" />
            <div className="flex-1 min-w-[180px]">
              <div className="text-sm font-medium">Esta receta se puede refinar</div>
              <div className="text-xs text-muted-foreground">
                Conviértela a versión profesional con elaboraciones base reutilizables (salsas, fondos…). Coste: 1 crédito.
              </div>
            </div>
            <Button
              size="sm"
              onClick={handleGenerateRecipeProfessional}
              disabled={proposing || generating}
              className="gap-1.5 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white"
              data-testid="refine-legacy-btn"
            >
              {proposing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              Refinar a profesional
            </Button>
          </div>
        )}

        {/* Generating */}
        {generating && (
          <div className="py-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Generando receta con IA...</p>
          </div>
        )}

        {/* Recipe/Beverage content — always render for beverages even without a recipe */}
        {(displayRecipe || isBeverage) && !generating && (
          <div className="space-y-6">

            {/* ── Image section ────────────────────────────────────────── */}
            <RecipeImageSection
              imgSrc={imgSrc}
              localDish={localDish}
              isEditing={isEditing}
              isBeverage={isBeverage}
              uploading={uploading}
              bevRegenLoading={bevRegenLoading}
              comparingUrl={comparingUrl}
              setComparingUrl={setComparingUrl}
              fileInputRef={fileInputRef}
              handleUploadImage={handleUploadImage}
              handleRestoreImage={handleRestoreImage}
              handleCancelBeverageRegen={handleCancelBeverageRegen}
            />

            {/* ── Recipe: Name edit ───────────────────────────────────── */}
            {!isBeverage && isEditing && (
              <div className="space-y-1.5" data-testid="recipe-name-section">
                <label htmlFor="recipe-edit-name" className="text-sm font-medium">
                  Nombre del plato
                </label>
                <Input
                  id="recipe-edit-name"
                  value={editedRecipe?._dish_name ?? localDish?.name ?? ""}
                  onChange={(e) => setEditedRecipe(prev => ({ ...(prev || {}), _dish_name: e.target.value }))}
                  placeholder="Nombre del plato"
                  data-testid="recipe-name-input"
                  className={nameConflict ? "border-amber-400 focus-visible:ring-amber-400" : ""}
                />
                {nameConflict && (
                  <div
                    className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2"
                    data-testid="recipe-name-conflict"
                  >
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                      Ya existe otro plato con este nombre en tu carta. Puede
                      causar confusión en el TPV — considera añadir un sufijo
                      (ej. "v2", "Casero", fecha…).
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* ── Basic Info ───────────────────────────────────────────── */}
            {!isBeverage && displayRecipe?.cost_recomputed_at && (() => {
              const ts = new Date(displayRecipe.cost_recomputed_at);
              const now = new Date();
              const diffMs = now - ts;
              const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
              const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
              if (diffDays > 14) return null;  // older than 2 weeks: no banner
              const label = diffHours < 1
                ? "ahora mismo"
                : diffHours < 24
                  ? `hace ${diffHours}h`
                  : diffDays === 1
                    ? "hace 1 día"
                    : `hace ${diffDays} días`;
              const reason = displayRecipe.cost_recompute_reason === "invoice_sync"
                ? "según tus facturas"
                : "automáticamente";
              return (
                <div
                  className="flex items-center gap-2 text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-3 py-2"
                  data-testid="recipe-cost-fresh-banner"
                  title={`Recalculado el ${ts.toLocaleString()}`}
                >
                  <TrendingUp className="w-3.5 h-3.5 shrink-0" />
                  <span>
                    <span className="font-medium">Coste actualizado {label}</span>{" "}
                    <span className="text-emerald-700">{reason}</span>
                  </span>
                </div>
              );
            })()}

            {!isBeverage && (
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-4 bg-muted/50 rounded-lg">
                <Users className="w-5 h-5 mx-auto mb-1 text-primary" />
                {isEditing ? (
                  <Input
                    type="number"
                    value={editedRecipe?.servings || 4}
                    onChange={(e) => setEditedRecipe(prev => ({ ...prev, servings: parseInt(e.target.value) || 1 }))}
                    className="text-center h-8 w-16 mx-auto"
                    min={1}
                  />
                ) : (
                  <p className="text-2xl font-bold">{displayRecipe.servings || 4}</p>
                )}
                <p className="text-sm text-muted-foreground">Raciones</p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <Clock className="w-5 h-5 mx-auto mb-1 text-primary" />
                {isEditing ? (
                  <Input
                    type="number"
                    value={editedRecipe?.preparation_time || 30}
                    onChange={(e) => setEditedRecipe(prev => ({ ...prev, preparation_time: parseInt(e.target.value) || 0 }))}
                    className="text-center h-8 w-16 mx-auto"
                    min={0}
                  />
                ) : (
                  <p className="text-2xl font-bold">{displayRecipe.preparation_time || 30}</p>
                )}
                <p className="text-sm text-muted-foreground">Minutos</p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <Euro className="w-5 h-5 mx-auto mb-1 text-primary" />
                <p className="text-2xl font-bold text-primary">
                  {formatCurrency(
                    (displayRecipe.ai_ingredients || displayRecipe.ingredients || [])
                      .reduce((sum, ing) => sum + (parseFloat(ing.estimated_cost) || 0), 0)
                    || displayRecipe.estimated_total_cost
                    || displayRecipe.total_cost
                    || 0
                  )}
                </p>
                <p className="text-sm text-muted-foreground">Coste est.</p>
              </div>
            </div>
            )}

            {/* ── Beverage: Name + Description block ──────────────────── */}
            {isBeverage && (
              <div className="space-y-4" data-testid="beverage-description-section">
                {isEditing && (
                  <div className="space-y-1.5">
                    <label htmlFor="bev-edit-name" className="text-sm font-medium">Nombre</label>
                    <Input
                      id="bev-edit-name"
                      value={editedRecipe?._dish_name ?? localDish?.name ?? ""}
                      onChange={(e) => setEditedRecipe(prev => ({ ...(prev || {}), _dish_name: e.target.value }))}
                      placeholder="Nombre de la bebida"
                      data-testid="beverage-name-input"
                      className={nameConflict ? "border-amber-400 focus-visible:ring-amber-400" : ""}
                    />
                    {nameConflict && (
                      <div
                        className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2"
                        data-testid="beverage-name-conflict"
                      >
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        <span>
                          Ya existe otra bebida o plato con este nombre en tu
                          carta. Puede causar confusión en el TPV.
                        </span>
                      </div>
                    )}
                  </div>
                )}
                <div className="space-y-2">
                  <h3 className="font-heading font-semibold flex items-center gap-2">
                    <ChefHat className="w-5 h-5 text-primary" />
                    Descripción
                  </h3>
                  {isEditing ? (
                    <Textarea
                      value={editedRecipe?._dish_description ?? localDish?.description ?? ""}
                      onChange={(e) => setEditedRecipe(prev => ({ ...(prev || {}), _dish_description: e.target.value }))}
                      placeholder="Describe esta bebida (ej. tipo, marca, notas de cata...)"
                      className="min-h-[100px]"
                      data-testid="beverage-description-textarea"
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground leading-relaxed" data-testid="beverage-description-text">
                      {typeof localDish?.description === "string" && localDish.description
                        ? localDish.description
                        : "Sin descripción. Pulsa Editar para añadir una."}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* ── Ingredients ──────────────────────────────────────────── */}
            {/* Base recipes block — only for professional recipes */}
            {!isBeverage && (displayRecipe?.base_recipe_refs || []).length > 0 && (
              <div data-testid="base-refs-block" className="border rounded-lg p-4 bg-gradient-to-br from-violet-50 to-indigo-50 dark:from-violet-950/30 dark:to-indigo-950/30 border-violet-200 dark:border-violet-900">
                <h3 className="font-heading font-semibold flex items-center gap-2 mb-3">
                  <Sparkles className="w-5 h-5 text-violet-600" />
                  Elaboraciones base usadas
                  <Badge variant="outline" className="text-[10px] bg-card/70 dark:bg-background/70">
                    {displayRecipe.base_recipe_refs.length}
                  </Badge>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {displayRecipe.base_recipe_refs.map((ref) => (
                    <div key={ref.batch_recipe_id}
                      data-testid={`base-ref-${ref.batch_recipe_id}`}
                      className="bg-background/80 dark:bg-background/60 border border-violet-200 dark:border-violet-900 rounded-md p-3">
                      <div className="font-medium text-sm">{ref.name || "Receta base"}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Consume {ref.quantity} {ref.unit}{ref.yield_qty ? ` · rinde ${ref.yield_qty} ${ref.yield_unit}` : ""}
                      </div>
                      {ref.notes && (
                        <div className="text-xs text-muted-foreground/80 italic mt-1 line-clamp-2">{ref.notes}</div>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-3 flex items-start gap-1.5">
                  <Lightbulb className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  Estas elaboraciones se gestionan en la pestaña <strong>Recetas Base</strong>. El coste de este plato se recalcula automáticamente si cambias las cantidades o los ingredientes de una base.
                </p>
              </div>
            )}


            {!isBeverage && (
            <div>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="font-heading font-semibold flex items-center gap-2">
                  <Package className="w-5 h-5 text-primary" />
                  Ingredientes
                  {(() => {
                    const list = displayRecipe?.ai_ingredients || displayRecipe?.ingredients || [];
                    const linkedCount = list.filter(i => i?.ingredient_id).length;
                    if (list.length === 0) return null;
                    return (
                      <Badge
                        variant="outline"
                        className={`text-[10px] ${linkedCount === list.length ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900" : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900"}`}
                        data-testid="recipe-linked-counter"
                      >
                        {linkedCount}/{list.length} vinculados
                      </Badge>
                    );
                  })()}
                  {isEditing && (
                    <Button size="sm" variant="ghost" onClick={addIngredient}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  )}
                </h3>
                {!isEditing && restaurantId && currentRecipe && (displayRecipe?.ai_ingredients || displayRecipe?.ingredients || []).some(i => !i?.ingredient_id) && (
                  <Button
                    type="button" size="sm" variant="outline"
                    onClick={handleAutoLink}
                    disabled={autoLinking}
                    className="gap-1.5"
                    data-testid="recipe-auto-link-btn"
                  >
                    {autoLinking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    {autoLinking ? "Vinculando…" : "Vincular con inventario"}
                  </Button>
                )}
              </div>
              {isEditing && (
                <MissingIngredientsBanner
                  ingredients={displayRecipe.ai_ingredients || displayRecipe.ingredients || []}
                  restaurantId={restaurantId}
                  autoFillingIds={autoFillingIds}
                  onCreate={handleAIAutofillAndLink}
                  onCreateAll={handleAIAutofillAll}
                />
              )}
              <div className="space-y-2">
                {(Array.isArray(displayRecipe.ai_ingredients) && displayRecipe.ai_ingredients.length > 0 ? displayRecipe.ai_ingredients : Array.isArray(displayRecipe.ingredients) ? displayRecipe.ingredients : []).map((ing, idx) => (
                  <RecipeIngredientRow
                    key={idx}
                    index={idx}
                    ingredient={ing}
                    inventoryIngredients={inventoryIngredients}
                    isEditing={isEditing}
                    suggestion={
                      unitMismatchSuggestions.find(
                        s => s.row_index === idx ||
                             (s.recipe_name && ing?.name && s.recipe_name.toLowerCase() === String(ing.name).toLowerCase())
                      ) || null
                    }
                    onDismissSuggestion={() => {
                      setUnitMismatchSuggestions(prev =>
                        prev.filter(s => s.row_index !== idx &&
                          !(s.recipe_name && ing?.name && s.recipe_name.toLowerCase() === String(ing.name).toLowerCase()))
                      );
                    }}
                    onChange={(i, patched) => {
                      const next = [...(editedRecipe.ai_ingredients || editedRecipe.ingredients || [])];
                      next[i] = patched;
                      setEditedRecipe(prev => ({ ...prev, ai_ingredients: next, ingredients: next }));
                    }}
                    onRemove={(i) => {
                      const next = (editedRecipe.ai_ingredients || editedRecipe.ingredients || []).filter((_, j) => j !== i);
                      setEditedRecipe(prev => ({ ...prev, ai_ingredients: next, ingredients: next }));
                    }}
                    onQuickAdd={(initialName) => {
                      setQuickAddContext({ rowIndex: idx, initialName });
                      setQuickAddOpen(true);
                    }}
                  />
                ))}
                {isEditing && (!displayRecipe.ai_ingredients || displayRecipe.ai_ingredients.length === 0) && (!displayRecipe.ingredients || displayRecipe.ingredients.length === 0) && (
                  <p className="text-sm text-muted-foreground italic text-center py-3">
                    Sin ingredientes. Pulsa + para añadir uno.
                  </p>
                )}
              </div>
            </div>
            )}

            {/* ── Steps ────────────────────────────────────────────────── */}
            {!isBeverage && (
            <div>
              <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                Preparación
                {isEditing && (
                  <Button size="sm" variant="ghost" onClick={addStep}>
                    <Plus className="w-4 h-4" />
                  </Button>
                )}
              </h3>
              <ol className="space-y-3">
                {(Array.isArray(displayRecipe.preparation_steps) ? displayRecipe.preparation_steps : Array.isArray(displayRecipe.steps) ? displayRecipe.steps : []).map((step, idx) => (
                  <li key={idx} className="flex gap-3 items-start">
                    <Badge className="h-6 w-6 flex items-center justify-center rounded-full flex-shrink-0 mt-1">
                      {idx + 1}
                    </Badge>
                    {isEditing ? (
                      <div className="flex-1 flex gap-2">
                        <Textarea value={step} onChange={(e) => updateStep(idx, e.target.value)} className="flex-1 min-h-[60px]" />
                        <Button size="icon" variant="ghost" onClick={() => removeStep(idx)}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    ) : (
                      <p className="text-sm">{step}</p>
                    )}
                  </li>
                ))}
              </ol>
            </div>
            )}

            {/* ── Tips ─────────────────────────────────────────────────── */}
            {!isBeverage && ((Array.isArray(displayRecipe.tips) && displayRecipe.tips.length > 0) || isEditing) && (
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <h3 className="font-heading font-semibold mb-2 text-yellow-700 dark:text-yellow-400 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5" />
                  Consejos del chef
                  {isEditing && (
                    <Button size="sm" variant="ghost" onClick={addTip} className="text-yellow-700">
                      <Plus className="w-4 h-4" />
                    </Button>
                  )}
                </h3>
                <ul className="space-y-2">
                  {(Array.isArray(displayRecipe.tips) ? displayRecipe.tips : []).map((tip, idx) => (
                    <li key={idx} className="flex gap-2 items-start">
                      {isEditing ? (
                        <>
                          <span className="text-yellow-700">•</span>
                          <Input value={tip} onChange={(e) => updateTip(idx, e.target.value)} className="flex-1 bg-card/50" />
                          <Button size="icon" variant="ghost" onClick={() => removeTip(idx)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </>
                      ) : (
                        <span className="text-sm text-yellow-700 dark:text-yellow-400">• {tip}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Footer actions */}
        {(displayRecipe || isBeverage) && !generating && (
          <DialogFooter className="flex-col sm:flex-row gap-2 mt-6">
            {isEditing ? (
              <>
                <Button variant="outline" onClick={() => {
                  setIsEditing(false);
                  setEditedRecipe(currentRecipe);
                }}>
                  <X className="w-4 h-4 mr-2" />
                  Cancelar
                </Button>
                <Button onClick={isBeverage ? handleSaveBeverage : handleSaveRecipe} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar cambios
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={isBeverage ? handleRegenerateBeverageImage : handleGenerateRecipe}
                  disabled={isBeverage ? bevRegenLoading : generating}
                >
                  {(isBeverage ? bevRegenLoading : false)
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generando...</>
                    : <><RefreshCw className="w-4 h-4 mr-2" />Regenerar con IA</>
                  }
                </Button>
                <Button
                  data-testid="edit-recipe-btn"
                  onClick={() => {
                    setIsEditing(true);
                    setEditedRecipe(
                      isBeverage
                        ? {
                            _dish_name: localDish?.name || "",
                            _dish_description: localDish?.description || "",
                          }
                        : {
                            ...normalizeRecipe(currentRecipe || displayRecipe),
                            _dish_name: localDish?.name || "",
                          }
                    );
                  }}
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  {isBeverage ? "Editar" : "Editar receta"}
                </Button>
              </>
            )}
          </DialogFooter>
        )}
      </DialogContent>

      {/* Quick-add ingredient to inventory (used from ingredient row) */}
      {restaurantId && (
        <QuickAddIngredientDialog
          open={quickAddOpen}
          onOpenChange={setQuickAddOpen}
          restaurantId={restaurantId}
          onCreated={async (created) => {
            setQuickAddOpen(false);
            // Refresh inventory list
            try {
              const list = await api.getIngredients(restaurantId);
              setInventoryIngredients(Array.isArray(list) ? list : []);
            } catch { /* noop */ }
            // Auto-link the created ingredient to the target row
            if (created?.ingredient_id && quickAddContext != null) {
              const idx = quickAddContext.rowIndex;
              const next = [...(editedRecipe?.ai_ingredients || editedRecipe?.ingredients || [])];
              const baseUnit = created.unit || "g";
              next[idx] = {
                ...(next[idx] || {}),
                name: created.name,
                ingredient_id: created.ingredient_id,
                unit: baseUnit,
                estimated_cost: (parseFloat(created.cost_per_unit) || 0) * (parseFloat(next[idx]?.quantity) || 0),
              };
              setEditedRecipe(prev => ({ ...prev, ai_ingredients: next, ingredients: next }));
              toast.success(`"${created.name}" vinculado a la receta`);
            }
            setQuickAddContext(null);
          }}
        />
      )}

      {/* Professional proposal dialog — after IA descompone el plato */}
      <ProfessionalProposalDialog
        open={!!proProposal}
        onOpenChange={(v) => !v && setProProposal(null)}
        restaurantId={restaurantId}
        dish={localDish}
        proposal={proProposal}
        onApplied={handleProApplied}
      />
    </Dialog>
  );
}
